import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/owner-dashboard — hand-rebuilt in brand primitives.
 *
 * Merchant "headquarters" — 5 tabs mirror the archive screen but use the
 * unified shell (no dark sidebar, no hard-coded Trattoria Giulia, no
 * duplicate chrome). Pulls the logged-in user's first owned restaurant and
 * seeds live KPI + top-dish + allergen-question + staff data from demo
 * values until the backend ships analytics endpoints.
 *
 * Tabs: Overview (KPIs + charts) | Menu | Specials | Allergens | Guests
 */

const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { id: 'overview',  label: 'Overview',  icon: 'chart',    tone: 'blue'   },
  { id: 'menu',      label: 'Menu',      icon: 'menu',     tone: 'blue'   },
  { id: 'specials',  label: 'Specials',  icon: 'flame',    tone: 'orange' },
  { id: 'allergens', label: 'Allergens', icon: 'shield',   tone: 'red'    },
  { id: 'guests',    label: 'Guests',    icon: 'people',   tone: 'purple' },
  { id: 'reviews',   label: 'Reviews',   icon: 'star',     tone: 'orange' },
  { id: 'staff',     label: 'Staff',     icon: 'badge',    tone: 'green'  },
];

const TONE_COLORS = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  red:    { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED' },
};

export default function UnifiedOwnerDashboard() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  // Analytics for the currently-selected restaurant. Null while loading
  // or before a restaurant is picked; an object from /api/owner/analytics
  // once it resolves. Fields the backend couldn't compute (covers,
  // revenue, top_dishes, etc.) come back as null — each tab decides
  // whether to fall back to demo or show a "not yet" state.
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.listRestaurants();
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : (list?.restaurants || []);
        setRestaurants(arr);
        if (arr.length) setSelectedId(String(arr[0].id));
      } catch {
        if (!cancelled) setRestaurants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  // Fetch analytics whenever the selected restaurant changes. Null on
  // error so the tabs fall back to their demo state — never a broken
  // dashboard during design review.
  useEffect(() => {
    if (!selectedId) { setAnalytics(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getOwnerAnalytics(selectedId);
        if (!cancelled) setAnalytics(data || null);
      } catch {
        if (!cancelled) setAnalytics(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const selected = useMemo(() => restaurants.find((r) => String(r.id) === String(selectedId)), [restaurants, selectedId]);

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{
            position: 'relative', overflow: 'hidden',
            padding: '22px 24px',
            borderRadius: 26,
            background: 'linear-gradient(160deg, #0A2540 0%, #0F5FB8 60%, #1E9BFF 100%)',
            color: '#fff',
            marginBottom: 18,
            boxShadow: '0 20px 44px rgba(10,37,64,0.3)',
          }}
        >
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.36,
          }}/>
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.82, marginBottom: 4 }}>
                OWNER DASHBOARD
              </div>
              <h1 style={{ margin: 0, fontSize: 32, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.05 }}>
                {selected?.name || (loading ? 'Loading…' : 'Your restaurant')}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                <LiveIndicator />
                <span style={{ fontSize: 13, fontWeight: 700, opacity: 0.88 }}>
                  {nowFormatted()} · tonight's service
                </span>
              </div>
            </div>
            {restaurants.length > 1 && (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                style={{
                  padding: '8px 32px 8px 14px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.3)',
                  fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800,
                  backdropFilter: 'blur(6px)',
                  cursor: 'pointer',
                  appearance: 'none',
                  outline: 'none',
                }}>
                {restaurants.map((r) => (
                  <option key={r.id} value={String(r.id)} style={{ color: 'var(--cwm-ink)' }}>{r.name}</option>
                ))}
              </select>
            )}
          </div>
        </motion.section>

        {/* Tab strip */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08, ease: EASE }}
          style={{
            display: 'flex', gap: 4, padding: 4,
            background: 'rgba(255,255,255,0.88)',
            border: '1px solid rgba(255,255,255,0.95)',
            borderRadius: 999,
            boxShadow: '0 8px 20px rgba(10,37,64,0.08)',
            marginBottom: 18,
            overflowX: 'auto',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          {TABS.map((t) => {
            const on = activeTab === t.id;
            const tone = TONE_COLORS[t.tone];
            return (
              // flex: 0 0 auto so each pill sizes to its label and the
              // parent's overflowX:auto lets the strip scroll horizontally
              // on narrow viewports instead of squashing labels into each
              // other. Previously `flex:1; min-width:0` was forcing the
              // tab row to compress below content width on mobile.
              <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                      style={{
                        flex: '0 0 auto',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        padding: '10px 14px', borderRadius: 999,
                        background: on ? `linear-gradient(90deg, ${tone.solid}, ${tone.solid})` : 'transparent',
                        color: on ? '#fff' : 'var(--cwm-gray-700)',
                        border: 'none', cursor: 'pointer',
                        fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900, whiteSpace: 'nowrap',
                        boxShadow: on ? `0 6px 14px ${tone.solid}44` : 'none',
                        transition: 'all 180ms ease',
                      }}>
                <TabIcon name={t.icon} size={14} /> {t.label}
              </button>
            );
          })}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.28, ease: EASE }}
          >
            {activeTab === 'overview'  && <OverviewTab restaurant={selected} analytics={analytics} />}
            {activeTab === 'menu'      && <MenuTab restaurant={selected} onOpen={() => navigate('/unified/menu-manager')} />}
            {activeTab === 'specials'  && <SpecialsTab onOpen={() => navigate('/unified/menu-manager')} />}
            {activeTab === 'allergens' && <AllergensTab analytics={analytics} />}
            {activeTab === 'guests'    && <GuestsTab />}
            {activeTab === 'reviews'   && <ReviewsTab restaurant={selected} analytics={analytics} />}
            {activeTab === 'staff'     && <StaffTab restaurant={selected} onTrain={() => navigate('/unified/server-dashboard')} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </AppShell>
  );
}

// ============================================================
// OVERVIEW TAB
// ============================================================

const DEMO_TOP_DISHES = [
  { rank: 1, name: 'Tagliatelle al Ragù',  sold: 24, revenue: 528 },
  { rank: 2, name: "Nonna's Tiramisu",     sold: 32, revenue: 320 },
  { rank: 3, name: 'Chianti glass',         sold: 28, revenue: 252 },
  { rank: 4, name: 'Burrata starter',       sold: 19, revenue: 228 },
  { rank: 5, name: 'Cacio e Pepe GF',       sold: 11, revenue: 220 },
];

const DEMO_ALLERGEN_Q = [
  { allergen: 'Gluten',    count: 18, tone: 'orange' },
  { allergen: 'Nuts',      count: 12, tone: 'red'    },
  { allergen: 'Dairy',     count:  9, tone: 'orange' },
  { allergen: 'Shellfish', count:  5, tone: 'purple' },
  { allergen: 'Soy',       count:  3, tone: 'green'  },
];

const DEMO_STAFF = [
  { initials: 'MR', name: 'Maya R.',   role: 'Server', score: 100, questions: 47, flag: null },
  { initials: 'TL', name: 'Tom L.',    role: 'Server', score:  92, questions: 38, flag: '1 dish mis-tagged, corrected' },
  { initials: 'PK', name: 'Priya K.',  role: 'Host',   score:  88, questions: 12, flag: null },
  { initials: 'JC', name: 'Jason C.',  role: 'Server', score:  84, questions: 28, flag: null },
];

const DEMO_HOURLY = [2, 4, 6, 8, 11, 14, 18, 22, 28, 36, 42, 58, 68, 74, 82];

function OverviewTab({ restaurant, analytics }) {
  // Real metrics come from `analytics` when available. Null-safe: we
  // keep demo values as fallbacks so an unselected restaurant / failed
  // fetch never shows an empty dashboard.
  //
  // Covers / Revenue / Avg check rely on Orders data we haven't shipped
  // — those stay demo until the Orders feature lands, which is why
  // there's a "from POS" hint on them.
  const menuChats = analytics?.kpis?.menu_chats;
  const safetySignals = analytics?.kpis?.safety_signals;
  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <KpiTile label="Covers"      value="82"     delta="+14"   deltaLabel="from POS (coming soon)" tone="blue"   />
        <KpiTile label="Revenue"     value="$4,218" delta="+18%"  deltaLabel="from POS (coming soon)" tone="green"  />
        <KpiTile label="Avg check"   value="$51"    delta="+$4"   deltaLabel="from POS (coming soon)" tone="orange" />
        <KpiTile
          label="Menu chats"
          value={typeof menuChats === 'number' ? String(menuChats) : '—'}
          delta={typeof safetySignals === 'number' ? String(safetySignals) : '0'}
          deltaLabel={typeof safetySignals === 'number' ? 'safety signals' : 'incidents'}
          tone="purple"
          invert
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 14 }}>

        {/* Covers per hour */}
        <div className="cwm-sheet" style={{ padding: 20 }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>COVERS PER HOUR</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 14 }}>
            Tonight vs last Thursday
          </div>
          <HourlyChart data={DEMO_HOURLY} />
        </div>

        {/* Top dishes */}
        <div className="cwm-sheet" style={{ padding: 20 }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)' }}>TONIGHT'S TOP DISHES</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 12 }}>
            By revenue
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {DEMO_TOP_DISHES.map((d, i) => (
              <motion.div
                key={d.rank}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + i * 0.05 }}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 10,
                  background: d.rank === 1 ? 'linear-gradient(160deg, #1E9BFF, #0F7FE8)' : 'var(--cwm-blue-pale)',
                  color: d.rank === 1 ? '#fff' : 'var(--cwm-blue-deep)',
                  display: 'grid', placeItems: 'center',
                  fontSize: 13, fontWeight: 900,
                }}>
                  {d.rank}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{d.sold} sold</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>
                  ${d.revenue}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>

        {/* Allergen questions */}
        <div className="cwm-sheet" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div>
              <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)' }}>ALLERGEN QUESTIONS TODAY</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
                Zero incidents reported
              </div>
            </div>
            <span style={{
              padding: '4px 10px', borderRadius: 999,
              background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
              fontSize: 10.5, fontWeight: 900, letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}>
              ● CLEAR
            </span>
          </div>
          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
            {(() => {
              // Real allergen-question counts come from
              // analytics.allergen_questions (table_questions grouped by
              // dietary_restriction). Falls back to the hand-rolled
              // DEMO_ALLERGEN_Q when the backend has nothing for this
              // restaurant yet, so the section is never empty.
              const live = analytics?.allergen_questions;
              const rows = (Array.isArray(live) && live.length > 0)
                ? live.map((r) => ({
                    allergen: r.allergen,
                    count: r.count,
                    // Heuristic tone: red for common allergens, orange
                    // otherwise. Keeps the bar chart's color semantics
                    // without demanding a tone from the backend.
                    tone: /(nut|peanut|tree.nut|shellfish)/i.test(r.allergen) ? 'red' : 'orange',
                  }))
                : DEMO_ALLERGEN_Q;
              const maxCount = rows[0]?.count || 1;
              return rows.map((a, i) => {
                const tone = TONE_COLORS[a.tone] || TONE_COLORS.blue;
                const pct = (a.count / maxCount) * 100;
                return (
                  <div key={a.allergen + i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 40px', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 900, color: tone.fg, textTransform: 'capitalize' }}>{a.allergen}</span>
                    <div style={{ height: 8, borderRadius: 999, background: 'var(--cwm-gray-100)', overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, delay: 0.15 + i * 0.05, ease: EASE }}
                        style={{ height: '100%', background: tone.solid, borderRadius: 999 }}/>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)', textAlign: 'right' }}>{a.count}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Staff activity */}
        <div className="cwm-sheet" style={{ padding: 20 }}>
          <div className="cwm-eyebrow" style={{ color: '#5B21B6' }}>STAFF ACTIVITY</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 14 }}>
            Last 4 shifts
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {DEMO_STAFF.map((s, i) => (
              <StaffRow key={s.initials} staff={s} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, delta, deltaLabel, tone, invert }) {
  const t = TONE_COLORS[tone] || TONE_COLORS.blue;
  const isGood = !invert && (delta.startsWith('+') || delta === '0');
  const deltaColor = invert ? t.fg : (isGood ? 'var(--cwm-green-text)' : 'var(--cwm-red-text)');
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 16 }}
    >
      <div className="cwm-eyebrow" style={{ color: t.fg }}>{label.toUpperCase()}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em', lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ fontSize: 12, fontWeight: 900, color: deltaColor }}>{delta}</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 3 }}>{deltaLabel}</div>
    </motion.div>
  );
}

function HourlyChart({ data }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ height: 140, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
      {data.map((v, i) => {
        const h = (v / max) * 100;
        return (
          <motion.div
            key={i}
            initial={{ height: 0 }} animate={{ height: `${h}%` }}
            transition={{ duration: 0.6, delay: 0.1 + i * 0.03, ease: EASE }}
            style={{
              flex: 1, minWidth: 0,
              background: 'linear-gradient(180deg, #1E9BFF, #0F7FE8)',
              borderRadius: '4px 4px 0 0',
              opacity: 0.4 + (v / max) * 0.6,
            }}/>
        );
      })}
    </div>
  );
}

function StaffRow({ staff, index }) {
  const goodScore = staff.score >= 95;
  const okScore   = staff.score >= 85;
  const scoreColor = goodScore ? 'var(--cwm-green-text)' : okScore ? 'var(--cwm-amber-text)' : 'var(--cwm-red-text)';
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.15 + index * 0.05 }}
      style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 12,
        background: 'linear-gradient(160deg, #F1E8FE, #CFBAF0)',
        color: '#5B21B6',
        display: 'grid', placeItems: 'center',
        fontSize: 12, fontWeight: 900, letterSpacing: '-0.01em', flexShrink: 0,
      }}>
        {staff.initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{staff.name}</div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
          {staff.role} · {staff.questions} Qs
          {staff.flag && <span style={{ color: 'var(--cwm-amber-text)' }}> · {staff.flag}</span>}
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 900, color: scoreColor, whiteSpace: 'nowrap' }}>
        {staff.score}%
      </div>
    </motion.div>
  );
}

function LiveIndicator() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', backdropFilter: 'blur(6px)' }}>
      <motion.span
        animate={{ opacity: [1, 0.4, 1] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        style={{ width: 7, height: 7, borderRadius: 999, background: '#4ADE80' }}/>
      Live
    </span>
  );
}

function nowFormatted() {
  const d = new Date();
  const day = d.toLocaleDateString(undefined, { weekday: 'long' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return `${day}, ${time}`;
}

// ============================================================
// MENU TAB
// ============================================================

function MenuTab({ restaurant, onOpen }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-blue-deep)' }}>MENU HEALTH</div>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 18 }}>
          Quality check
        </div>
        <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
          <HealthRow label="Items tagged with allergens"      value="94%" tone="green"  />
          <HealthRow label="Items with descriptions"          value="88%" tone="blue"   />
          <HealthRow label="Prices set"                       value="100%" tone="green" />
          <HealthRow label="Dietary tags present"             value="72%" tone="orange" />
          <HealthRow label="Items needing review"             value="3"   tone="red"    />
        </div>
        <button onClick={onOpen} className="cwm-btn" style={{ width: '100%', justifyContent: 'center' }}>
          <PencilIcon size={14} /> Open Menu Manager
        </button>
      </div>

      <div className="cwm-sheet" style={{ padding: 22 }}>
        <div className="cwm-eyebrow" style={{ color: 'var(--cwm-amber-text)' }}>QUICK ACTIONS</div>
        <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 18 }}>
          Common menu moves
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          <QuickLink icon="menu"   label="Edit menu items"       sub="Add items, change prices, tag allergens" onClick={onOpen} />
          <QuickLink icon="flame"  label="Publish a special"     sub="Tonight only, weekend, or chef's special" onClick={onOpen} />
          <QuickLink icon="lightbulb" label="Tune upsell tips"   sub="Scripted pairings your servers should use" onClick={onOpen} />
          <QuickLink icon="store"  label="Edit restaurant profile" sub="Hours, address, amenities, phone"       onClick={onOpen} />
        </div>
      </div>
    </div>
  );
}

function HealthRow({ label, value, tone }) {
  const t = TONE_COLORS[tone] || TONE_COLORS.blue;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 12, background: t.bg }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-ink)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 900, color: t.fg }}>{value}</span>
    </div>
  );
}

function QuickLink({ icon, label, sub, onClick }) {
  return (
    <motion.button type="button" onClick={onClick}
      whileHover={{ x: 3 }} whileTap={{ scale: 0.98 }}
      style={{
        padding: '12px 14px', borderRadius: 14,
        background: '#fff', color: 'var(--cwm-ink)',
        border: '1.5px solid var(--cwm-gray-200)',
        fontFamily: 'var(--cwm-font)', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 12,
        textAlign: 'left',
      }}>
      <span style={{
        width: 36, height: 36, borderRadius: 10,
        background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <TabIcon name={icon} size={16} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>{sub}</span>
      </span>
      <ArrowIcon size={14} color="var(--cwm-gray-400)" />
    </motion.button>
  );
}

// ============================================================
// SPECIALS TAB
// ============================================================

const DEMO_SPECIALS = [
  { id: 1, name: 'Seafood Risotto',    description: 'Saffron risotto, scallops, shrimp, mussels. Tonight only.', price: 3800, days: ['Thu', 'Fri', 'Sat'], live: true },
  { id: 2, name: 'Half bottle of Chianti', description: '$8 off any half bottle with entrée. Thu–Sun.', price: 2200, days: ['Thu', 'Fri', 'Sat', 'Sun'], live: true },
  { id: 3, name: "Chef's tasting menu",  description: '5-course, allergy-adapted on request. Fri/Sat 6-9pm.', price: 9500, days: ['Fri', 'Sat'], live: false },
];

function SpecialsTab({ onOpen }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-amber-text)' }}>FEATURED TONIGHT</div>
          <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
            {DEMO_SPECIALS.filter((s) => s.live).length} live · {DEMO_SPECIALS.length - DEMO_SPECIALS.filter((s) => s.live).length} scheduled
          </div>
        </div>
        <button className="cwm-btn" onClick={onOpen}>
          <PlusIcon size={12} /> Add special
        </button>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        {DEMO_SPECIALS.map((s, i) => <MerchantSpecialCard key={s.id} special={s} index={i} onEdit={onOpen} />)}
      </div>
    </div>
  );
}

function MerchantSpecialCard({ special, index, onEdit }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: EASE }}
      style={{
        padding: 20, borderRadius: 20,
        background: special.live ? 'linear-gradient(160deg, #FFF4E0 0%, #FFE0B8 100%)' : '#fff',
        border: `1.5px solid ${special.live ? 'rgba(180,83,9,0.24)' : 'var(--cwm-gray-200)'}`,
        boxShadow: special.live ? '0 10px 24px rgba(180,83,9,0.14)' : '0 6px 14px rgba(13,71,161,0.06)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 999,
              background: '#fff', color: '#B45309',
              fontSize: 10.5, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>
              <FlameIcon size={10} /> SPECIAL
            </div>
            {special.live ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.06em' }}>
                <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.6, repeat: Infinity }} style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--cwm-green)' }}/> LIVE
              </span>
            ) : (
              <span style={{ padding: '3px 10px', borderRadius: 999, background: 'var(--cwm-gray-100)', color: 'var(--cwm-gray-700)', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.06em' }}>
                SCHEDULED
              </span>
            )}
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {special.name}
          </h3>
        </div>
        <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap' }}>
          ${(special.price / 100).toFixed(2)}
        </div>
      </div>
      {special.description && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.5 }}>
          {special.description}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => {
            const on = (special.days || []).includes(d);
            return (
              <span key={d} style={{
                padding: '3px 8px', borderRadius: 6,
                background: on ? '#B45309' : 'rgba(180,83,9,0.12)',
                color: on ? '#fff' : '#8A4A00',
                fontSize: 10, fontWeight: 900, letterSpacing: '0.04em',
              }}>{d}</span>
            );
          })}
        </div>
        <button type="button" onClick={onEdit}
                style={{
                  padding: '6px 14px', borderRadius: 999,
                  background: '#fff', color: 'var(--cwm-blue-deep)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  cursor: 'pointer',
                  fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
                }}>
          Edit
        </button>
      </div>
    </motion.article>
  );
}

// ============================================================
// ALLERGENS TAB
// ============================================================

const DEMO_ALLERGEN_TRAIL = [
  { time: '6:08 pm', guest: 'Table 7', allergen: 'Peanut',    asked_about: 'Thai chicken salad', resolved: true,  outcome: 'Subbed dressing — confirmed safe' },
  { time: '6:22 pm', guest: 'Table 12', allergen: 'Gluten',   asked_about: 'Pasta dishes',       resolved: true,  outcome: 'Recommended risotto' },
  { time: '7:14 pm', guest: 'Table 3',  allergen: 'Shellfish', asked_about: 'Seafood risotto',   resolved: true,  outcome: 'Ordered alternate' },
  { time: '7:46 pm', guest: 'Table 9',  allergen: 'Dairy',     asked_about: 'Tiramisu',          resolved: false, outcome: 'Awaiting chef confirmation' },
  { time: '8:02 pm', guest: 'Table 15', allergen: 'Nuts',      asked_about: 'Pesto dishes',      resolved: true,  outcome: 'Flagged; nut-free pesto available' },
];

function AllergensTab({ analytics }) {
  // Total allergen-questions from the real aggregator (table_questions
  // for this restaurant). Falls back to the demo trail length when
  // not available. Resolved / pending / incidents remain demo until
  // we have service-floor question tracking wired.
  const liveTotal = analytics?.allergen_questions
    ? analytics.allergen_questions.reduce((n, q) => n + q.count, 0)
    : null;
  const totalQs = typeof liveTotal === 'number' ? liveTotal : DEMO_ALLERGEN_TRAIL.length;
  const resolved = DEMO_ALLERGEN_TRAIL.filter((t) => t.resolved).length;
  const pending  = DEMO_ALLERGEN_TRAIL.length - resolved;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
        <KpiTile label="Questions (all time)" value={totalQs} delta={typeof liveTotal === 'number' ? 'real' : '+2'} deltaLabel={typeof liveTotal === 'number' ? 'from diner Q&A' : 'vs last Thu'} tone="blue" />
        <KpiTile label="Resolved"          value={resolved}                    delta="100%" deltaLabel="confirmed safe" tone="green" />
        <KpiTile label="Pending"           value={pending}                     delta={pending ? 'needs review' : 'clear'} deltaLabel="since opening" tone={pending ? 'red' : 'green'} invert />
        <KpiTile label="Incidents"         value="0" delta="0" deltaLabel="this month" tone="green" />
      </div>

      <div className="cwm-sheet" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cwm-gray-100)' }}>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-red-text)' }}>ALLERGEN LOG</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2 }}>
            Every guest question, resolved or pending
          </div>
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {DEMO_ALLERGEN_TRAIL.map((t, i) => (
            <li key={i} style={{
              padding: '14px 20px',
              borderBottom: i < DEMO_ALLERGEN_TRAIL.length - 1 ? '1px solid var(--cwm-gray-100)' : 'none',
              display: 'grid', gridTemplateColumns: '70px 1fr auto', alignItems: 'center', gap: 14,
            }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{t.time}</div>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--cwm-gray-500)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t.guest}</div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    padding: '3px 9px', borderRadius: 999,
                    background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
                    fontSize: 11, fontWeight: 900, letterSpacing: '0.04em',
                  }}>⚠ {t.allergen}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--cwm-gray-700)' }}>
                    about <strong style={{ color: 'var(--cwm-ink)' }}>{t.asked_about}</strong>
                  </span>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cwm-gray-700)' }}>
                  {t.outcome}
                </div>
              </div>
              <span style={{
                padding: '4px 10px', borderRadius: 999,
                background: t.resolved ? 'var(--cwm-green-light)' : 'var(--cwm-amber-light)',
                color: t.resolved ? 'var(--cwm-green-text)' : 'var(--cwm-amber-text)',
                fontSize: 10.5, fontWeight: 900, letterSpacing: '0.06em',
                whiteSpace: 'nowrap',
              }}>
                {t.resolved ? '✓ RESOLVED' : '○ PENDING'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ============================================================
// GUESTS TAB
// ============================================================

const DEMO_GUESTS = [
  { initials: 'RK', name: 'Rachel K.',     last: 'tonight',         visits: 8, restrictions: ['Peanut allergy', 'Dairy-Free'], note: 'Dines with 2 kids. Always asks for the chef.' },
  { initials: 'JD', name: 'John D.',       last: '3 days ago',       visits: 14, restrictions: ['Celiac'],                       note: 'Leaves 5-star reviews when we nail GF pasta.' },
  { initials: 'SM', name: 'Sarah M.',      last: 'last week',        visits: 4,  restrictions: ['Vegan'],                         note: 'Interested in chef\'s tasting if we can adapt.' },
  { initials: 'AT', name: 'Alex T.',       last: '2 weeks ago',      visits: 22, restrictions: ['Tree nut allergy'],              note: 'Regular. Orders the same thing every time.' },
];

function GuestsTab() {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
        <KpiTile label="Returning guests" value="64%"  delta="+8pp"  deltaLabel="vs last month" tone="green" />
        <KpiTile label="Avg visits/guest" value="3.2"  delta="+0.4"  deltaLabel="this quarter"   tone="blue"  />
        <KpiTile label="Safety profiles"  value="48"   delta="+12"   deltaLabel="active"         tone="purple"/>
        <KpiTile label="Reviews"          value="4.7★" delta="+0.2"  deltaLabel="vs last 90d"    tone="orange" />
      </div>

      <div>
        <div className="cwm-eyebrow" style={{ color: '#5B21B6', marginBottom: 10 }}>REGULAR GUESTS</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {DEMO_GUESTS.map((g, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              whileHover={{ y: -2 }}
              className="cwm-sheet"
              style={{ padding: 16 }}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12,
                  background: 'linear-gradient(160deg, #F1E8FE, #CFBAF0)',
                  color: '#5B21B6',
                  display: 'grid', placeItems: 'center',
                  fontSize: 13, fontWeight: 900, flexShrink: 0,
                }}>
                  {g.initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{g.name}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{g.visits} visits · last {g.last}</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                {g.restrictions.map((r) => (
                  <span key={r} style={{
                    padding: '3px 9px', borderRadius: 999,
                    background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
                    fontSize: 10.5, fontWeight: 800,
                  }}>⚠ {r}</span>
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cwm-gray-700)', fontStyle: 'italic', lineHeight: 1.45, padding: '8px 10px', borderLeft: '3px solid var(--cwm-blue-light)', background: 'var(--cwm-blue-pale)', borderRadius: '0 8px 8px 0' }}>
                "{g.note}"
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REVIEWS TAB — read, filter, and reply to diner reviews
// ============================================================

const DEMO_REVIEW_INBOX = [
  { id: 'r1', name: 'Priya S.',  initials: 'PS', rating: 5, safety: 5, daysAgo: 1,  tags: ['Gluten-Free', 'Peanut'], text: "The server walked me through every dish I couldn't have — felt safer than at my go-to spot. The pappardelle is unreal.",       replied: false, flagged: false, channel: 'chat' },
  { id: 'r2', name: 'Marcus T.', initials: 'MT', rating: 3, safety: 4, daysAgo: 2,  tags: ['Shellfish'],             text: "Got a clear answer on shellfish prep but the risotto came out lukewarm. Staff tried hard.",                                  replied: false, flagged: true,  channel: 'chat' },
  { id: 'r3', name: 'Hannah K.', initials: 'HK', rating: 5, safety: 5, daysAgo: 4,  tags: ['Tree nuts'],             text: "Brought my nut-allergic kid here — chef came out to talk about prep methods. That alone is worth 5 stars.",                  replied: true,  flagged: false, reply: 'Thanks Hannah — so glad the team could meet you at the table. See you next time!', channel: 'google' },
  { id: 'r4', name: 'Diego R.',  initials: 'DR', rating: 5, safety: 5, daysAgo: 6,  tags: ['Dairy'],                 text: "Best dairy-free tiramisu I've had. They labeled every dish on the menu. Ownership clearly gets it.",                          replied: false, flagged: false, channel: 'chat' },
  { id: 'r5', name: 'Alice W.',  initials: 'AW', rating: 2, safety: 4, daysAgo: 9,  tags: ['Gluten-Free'],           text: "The GF menu felt short — only two mains. Service was great and nothing made me sick, just disappointed by the options.",      replied: false, flagged: true,  channel: 'google' },
  { id: 'r6', name: 'Omar J.',   initials: 'OJ', rating: 5, safety: 5, daysAgo: 12, tags: ['Vegan'],                 text: 'Plant-based menu was genuinely creative. The kitchen is clearly trying new things — not just a salad and a burger.',           replied: false, flagged: false, channel: 'chat' },
];

function ReviewsTab({ restaurant, analytics }) {
  // Prefer real reviews from the analytics payload (fetched by the
  // parent). Fall back to demo when the list is empty or analytics
  // hasn't resolved yet, so the screen is always populated for design
  // review. Replies / flags stay optimistic-local — there's no
  // reply-to-review backend endpoint yet, and wiring one is its own
  // slice.
  const liveReviews = analytics?.review_inbox;
  const initialReviews = useMemo(() => {
    if (Array.isArray(liveReviews) && liveReviews.length > 0) {
      return liveReviews.map((r) => {
        const name = r.user_name || 'Diner';
        const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
        const daysAgo = r.datetime
          ? Math.max(0, Math.floor((Date.now() - new Date(r.datetime).getTime()) / 86400000))
          : 0;
        return {
          id: `r-${r.id}`,
          name,
          initials,
          rating: r.rating,
          safety: r.safety_rating ?? r.rating,
          daysAgo,
          tags: r.allergen_tags || [],
          text: r.review_text,
          replied: false,
          flagged: false,
          channel: 'chat',
        };
      });
    }
    return DEMO_REVIEW_INBOX;
  }, [liveReviews]);

  const [reviews, setReviews] = useState(initialReviews);
  // Keep `reviews` in sync if analytics arrives after first render.
  useEffect(() => { setReviews(initialReviews); }, [initialReviews]);
  const [filter, setFilter] = useState('all'); // all | unreplied | 1-2-stars | flagged
  const [replyingId, setReplyingId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');

  const stats = useMemo(() => {
    const n = reviews.length;
    const avg = n ? (reviews.reduce((s, r) => s + r.rating, 0) / n) : 0;
    const unreplied = reviews.filter((r) => !r.replied && !r.flagged).length;
    const flagged = reviews.filter((r) => r.flagged).length;
    const lowStar = reviews.filter((r) => r.rating <= 2).length;
    return { n, avg: avg.toFixed(1), unreplied, flagged, lowStar };
  }, [reviews]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'unreplied': return reviews.filter((r) => !r.replied);
      case '1-2-stars': return reviews.filter((r) => r.rating <= 2);
      case 'flagged':   return reviews.filter((r) => r.flagged);
      case 'all':
      default:          return reviews;
    }
  }, [reviews, filter]);

  const startReply = (r) => {
    setReplyingId(r.id);
    setReplyDraft(r.reply || '');
  };
  const submitReply = () => {
    setReviews((arr) => arr.map((r) => r.id === replyingId ? { ...r, replied: true, reply: replyDraft.trim(), flagged: false } : r));
    setReplyingId(null);
    setReplyDraft('');
  };
  const toggleFlag = (id) => setReviews((arr) => arr.map((r) => r.id === id ? { ...r, flagged: !r.flagged } : r));

  const FILTERS = [
    { id: 'all',       label: 'All',          count: stats.n },
    { id: 'unreplied', label: 'Needs reply',  count: stats.unreplied },
    { id: '1-2-stars', label: '1–2 stars',    count: stats.lowStar },
    { id: 'flagged',   label: 'Flagged',      count: stats.flagged },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <SummaryCard label="Avg rating"   value={`${stats.avg}★`} tone="orange" hint={`${stats.n} reviews`} />
        <SummaryCard label="Needs reply"  value={stats.unreplied} tone="blue"   hint="In last 30 days" />
        <SummaryCard label="1–2 stars"    value={stats.lowStar}   tone="red"    hint="Worth addressing" />
        <SummaryCard label="Flagged"      value={stats.flagged}   tone="purple" hint="By you, to revisit" />
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => {
          const on = filter === f.id;
          return (
            <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                    style={{
                      padding: '8px 14px', borderRadius: 999,
                      background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : '#fff',
                      color: on ? '#fff' : 'var(--cwm-blue-deep)',
                      border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
                      fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                      boxShadow: on ? '0 4px 12px rgba(15,127,232,0.25)' : 'none',
                    }}>
              {f.label}
              <span style={{
                padding: '1px 7px', borderRadius: 999,
                background: on ? 'rgba(255,255,255,0.22)' : 'var(--cwm-gray-100)',
                color: on ? '#fff' : 'var(--cwm-gray-700)',
                fontSize: 10, fontWeight: 900,
              }}>{f.count}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center', color: 'var(--cwm-gray-500)' }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 6 }}>Nothing here</div>
          No reviews match this filter. Try "All" to see everything.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map((r) => (
            <ReviewInboxCard
              key={r.id}
              review={r}
              onStartReply={() => startReply(r)}
              onToggleFlag={() => toggleFlag(r.id)}
              isReplying={replyingId === r.id}
              replyDraft={replyDraft}
              onDraftChange={setReplyDraft}
              onCancelReply={() => { setReplyingId(null); setReplyDraft(''); }}
              onSubmitReply={submitReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewInboxCard({ review, onStartReply, onToggleFlag, isReplying, replyDraft, onDraftChange, onCancelReply, onSubmitReply }) {
  const r = review;
  const channelColor = r.channel === 'google' ? { bg: '#FEF3C7', fg: '#92400E', border: '#FCD34D' }
                                              : { bg: 'var(--cwm-blue-pale)', fg: 'var(--cwm-blue-deep)', border: 'var(--cwm-blue-light)' };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, ease: EASE }}
      className="cwm-sheet" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 12,
          background: `linear-gradient(160deg, hsl(${(r.initials.charCodeAt(0) * 47) % 360} 70% 60%), hsl(${((r.initials.charCodeAt(0) + 3) * 47) % 360} 65% 45%))`,
          color: '#fff', display: 'grid', placeItems: 'center',
          fontSize: 12, fontWeight: 900, flexShrink: 0,
        }}>
          {r.initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{r.name}</div>
            <StarTiny value={r.rating} />
            <span style={{
              padding: '1px 8px', borderRadius: 999,
              background: channelColor.bg, color: channelColor.fg,
              border: `1px solid ${channelColor.border}`,
              fontSize: 9.5, fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>{r.channel}</span>
            {r.flagged && (
              <span style={{
                padding: '1px 8px', borderRadius: 999,
                background: '#F1E8FE', color: '#5B21B6', border: '1px solid #CFBAF0',
                fontSize: 9.5, fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: 3,
              }}><FlagIcon size={9} /> Flagged</span>
            )}
            {r.replied && (
              <span style={{
                padding: '1px 8px', borderRadius: 999,
                background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                border: '1px solid #A6E8C2',
                fontSize: 9.5, fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>✓ Replied</span>
            )}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {r.daysAgo === 0 ? 'today' : r.daysAgo === 1 ? 'yesterday' : `${r.daysAgo}d ago`} · Safety {r.safety}/5
          </div>
        </div>
      </div>

      <p style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.55 }}>
        "{r.text}"
      </p>

      {r.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
          {r.tags.map((t) => (
            <span key={t} style={{
              padding: '2px 9px', borderRadius: 999,
              background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
              fontSize: 10, fontWeight: 800,
            }}>⚠ {t}</span>
          ))}
        </div>
      )}

      {r.replied && r.reply && !isReplying && (
        <div style={{
          marginTop: 12, padding: '10px 14px', borderRadius: 12,
          background: 'var(--cwm-blue-pale)', borderLeft: '3px solid var(--cwm-blue-deep)',
          fontSize: 12.5, fontWeight: 600, color: 'var(--cwm-gray-700)', lineHeight: 1.5,
        }}>
          <div className="cwm-eyebrow" style={{ fontSize: 9.5, color: 'var(--cwm-blue-deep)', marginBottom: 4 }}>YOUR REPLY</div>
          "{r.reply}"
        </div>
      )}

      {isReplying ? (
        <div style={{ marginTop: 12 }}>
          <textarea
            value={replyDraft}
            onChange={(e) => onDraftChange(e.target.value)}
            rows={3}
            placeholder="Thanks for the feedback — we appreciate…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 14px', borderRadius: 12,
              border: '1.5px solid var(--cwm-blue-light)',
              background: '#F7FBFF', color: 'var(--cwm-ink)',
              fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 500, lineHeight: 1.5,
              outline: 'none', resize: 'vertical',
            }}/>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onCancelReply}
                    style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', color: 'var(--cwm-gray-500)', border: '1.5px solid var(--cwm-gray-200)', fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="button" onClick={onSubmitReply} disabled={!replyDraft.trim()}
                    style={{
                      padding: '7px 14px', borderRadius: 999,
                      background: replyDraft.trim() ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : 'var(--cwm-gray-100)',
                      color: replyDraft.trim() ? '#fff' : 'var(--cwm-gray-500)',
                      border: 'none', fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
                      cursor: replyDraft.trim() ? 'pointer' : 'not-allowed',
                      boxShadow: replyDraft.trim() ? '0 4px 10px rgba(15,127,232,0.3)' : 'none',
                    }}>
              {r.replied ? 'Update reply' : 'Send reply'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--cwm-gray-100)', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onToggleFlag}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: r.flagged ? '#F1E8FE' : 'transparent',
                    color: r.flagged ? '#5B21B6' : 'var(--cwm-gray-500)',
                    border: `1.5px solid ${r.flagged ? '#CFBAF0' : 'var(--cwm-gray-200)'}`,
                    fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
            <FlagIcon size={10} /> {r.flagged ? 'Unflag' : 'Flag'}
          </button>
          <button type="button" onClick={onStartReply}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                    border: '1.5px solid var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
            <ReplyIcon size={10} /> {r.replied ? 'Edit reply' : 'Reply'}
          </button>
        </div>
      )}
    </motion.div>
  );
}

function SummaryCard({ label, value, tone, hint }) {
  const t = TONE_COLORS[tone] || TONE_COLORS.blue;
  return (
    <div className="cwm-sheet" style={{ padding: 14 }}>
      <div className="cwm-eyebrow" style={{ color: t.fg }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em', margin: '4px 0 2px' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{hint}</div>
    </div>
  );
}

function StarTiny({ value = 0, size = 10 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= value ? '#F6B93B' : 'none'} stroke={i <= value ? '#F6B93B' : 'var(--cwm-gray-300)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18l-6.2 3 1.2-6.8-5-4.9 6.9-1z"/>
        </svg>
      ))}
    </span>
  );
}

function FlagIcon({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V4h14l-2 5 2 5H4"/></svg>; }
function ReplyIcon({ size = 10 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 17l-6-6 6-6M3 11h13a5 5 0 015 5v4"/></svg>; }

// ============================================================
// STAFF TAB — roster + invites + shift scheduling
// ============================================================

const ROLES = [
  { id: 'manager', label: 'Manager' },
  { id: 'server',  label: 'Server' },
  { id: 'host',    label: 'Host' },
  { id: 'runner',  label: 'Runner' },
  { id: 'bar',     label: 'Bartender' },
  { id: 'kitchen', label: 'Kitchen' },
];

const SHIFT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHIFT_BLOCKS = [
  { id: 'open',    label: 'Open',    tone: 'blue',   time: '10a–3p' },
  { id: 'mid',     label: 'Mid',     tone: 'orange', time: '3p–6p'  },
  { id: 'dinner',  label: 'Dinner',  tone: 'purple', time: '5p–10p' },
  { id: 'close',   label: 'Close',   tone: 'red',    time: '10p–2a' },
];

const DEMO_STAFF_FULL = [
  { id: 's1', initials: 'MR', name: 'Maya R.',   role: 'server',  email: 'maya@example.com',  phone: '555-0101', score: 100, questions: 47, certifications: ['allergy', 'wine'],         shifts: { Mon: 'dinner', Tue: 'dinner', Wed: null,     Thu: 'dinner', Fri: 'dinner', Sat: 'dinner', Sun: null     } },
  { id: 's2', initials: 'TL', name: 'Tom L.',    role: 'server',  email: 'tom@example.com',   phone: '555-0102', score:  92, questions: 38, certifications: ['allergy'],                 shifts: { Mon: 'open',   Tue: 'open',   Wed: 'mid',    Thu: null,     Fri: 'mid',    Sat: 'dinner', Sun: 'open'   } },
  { id: 's3', initials: 'PK', name: 'Priya K.',  role: 'host',    email: 'priya@example.com', phone: '555-0103', score:  88, questions: 12, certifications: [],                          shifts: { Mon: 'dinner', Tue: null,     Wed: 'dinner', Thu: 'dinner', Fri: 'dinner', Sat: 'dinner', Sun: 'dinner' } },
  { id: 's4', initials: 'JC', name: 'Jason C.',  role: 'server',  email: 'jason@example.com', phone: '555-0104', score:  84, questions: 28, certifications: ['allergy'],                 shifts: { Mon: null,     Tue: 'dinner', Wed: 'dinner', Thu: 'dinner', Fri: null,     Sat: 'open',   Sun: 'dinner' } },
  { id: 's5', initials: 'AB', name: 'Ana B.',    role: 'manager', email: 'ana@example.com',   phone: '555-0105', score:  98, questions:  9, certifications: ['allergy', 'wine', 'pos'], shifts: { Mon: 'open',   Tue: 'open',   Wed: 'open',   Thu: 'open',   Fri: 'open',   Sat: null,     Sun: null     } },
];

function StaffTab({ restaurant, onTrain }) {
  const [staff, setStaff] = useState(DEMO_STAFF_FULL);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [view, setView] = useState('roster');  // 'roster' | 'schedule'
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editStaff, setEditStaff] = useState(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return staff.filter((s) => {
      if (roleFilter !== 'all' && s.role !== roleFilter) return false;
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
    });
  }, [staff, query, roleFilter]);

  const upsert = (next) => setStaff((arr) => {
    const i = arr.findIndex((x) => x.id === next.id);
    if (i >= 0) { const c = arr.slice(); c[i] = next; return c; }
    return [...arr, next];
  });
  const removeStaff = (id) => {
    if (!window.confirm('Remove this team member?')) return;
    setStaff((arr) => arr.filter((s) => s.id !== id));
  };
  const assignShift = (staffId, day, blockId) => {
    setStaff((arr) => arr.map((s) => s.id === staffId ? { ...s, shifts: { ...s.shifts, [day]: blockId } } : s));
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="cwm-eyebrow" style={{ color: 'var(--cwm-green-text)' }}>STAFF</div>
          <h2 style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
            Your team — {staff.length} active
          </h2>
        </div>
        <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={onTrain}
                  style={{
                    padding: '9px 14px', borderRadius: 999,
                    background: '#fff', color: 'var(--cwm-blue-deep)',
                    border: '1.5px solid var(--cwm-blue-light)',
                    fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}>
            <QuizIcon size={12} /> Training quiz
          </button>
          <button type="button" onClick={() => setInviteOpen(true)} className="cwm-btn" style={{ padding: '9px 14px', fontSize: 12.5 }}>
            <PlusIcon size={12} /> Invite
          </button>
        </div>
      </div>

      {/* View switcher */}
      <div style={{ display: 'inline-flex', gap: 6, padding: 4, background: '#fff', borderRadius: 999, border: '1.5px solid var(--cwm-blue-light)', alignSelf: 'flex-start' }}>
        {[{ id: 'roster', label: 'Roster' }, { id: 'schedule', label: 'Shift schedule' }].map((v) => {
          const on = view === v.id;
          return (
            <button key={v.id} type="button" onClick={() => setView(v.id)}
                    style={{
                      padding: '7px 16px', borderRadius: 999,
                      background: on ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : 'transparent',
                      color: on ? '#fff' : 'var(--cwm-blue-deep)',
                      border: 'none', fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 900,
                      cursor: 'pointer',
                    }}>
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      {view === 'roster' && (
        <div className="cwm-sheet" style={{ padding: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', position: 'relative' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-gray-500)' }}>
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 14px 10px 34px',
                borderRadius: 999, border: '1.5px solid var(--cwm-blue-light)',
                background: '#F7FBFF', color: 'var(--cwm-ink)',
                fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 600, outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {['all', ...ROLES.map((r) => r.id)].map((rid) => {
              const on = roleFilter === rid;
              const label = rid === 'all' ? 'All roles' : ROLES.find((r) => r.id === rid)?.label || rid;
              return (
                <button key={rid} type="button" onClick={() => setRoleFilter(rid)}
                        style={{
                          padding: '6px 12px', borderRadius: 999,
                          background: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-50)',
                          color: on ? '#fff' : 'var(--cwm-gray-700)',
                          border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-200)'}`,
                          fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                          cursor: 'pointer',
                        }}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'roster' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map((s) => (
            <StaffCard key={s.id} staff={s} onEdit={() => setEditStaff(s)} onRemove={() => removeStaff(s.id)} />
          ))}
          {filtered.length === 0 && (
            <div className="cwm-sheet" style={{ padding: 40, textAlign: 'center', color: 'var(--cwm-gray-500)', gridColumn: '1 / -1' }}>
              No staff match your filter.
            </div>
          )}
        </div>
      ) : (
        <ShiftSchedule staff={staff} onAssign={assignShift} />
      )}

      <AnimatePresence>
        {inviteOpen && (
          <InviteStaffDialog
            restaurant={restaurant}
            onClose={() => setInviteOpen(false)}
            onInvite={(payload) => {
              const initials = (payload.name || '??').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
              upsert({
                id: `s-${Date.now()}`,
                initials,
                name: payload.name,
                role: payload.role,
                email: payload.email,
                phone: '',
                score: 0, questions: 0, certifications: [],
                shifts: SHIFT_DAYS.reduce((acc, d) => ({ ...acc, [d]: null }), {}),
                invited: true,
              });
              setInviteOpen(false);
            }}
          />
        )}
        {editStaff && (
          <EditStaffDialog
            staff={editStaff}
            onClose={() => setEditStaff(null)}
            onSave={(next) => { upsert(next); setEditStaff(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function StaffCard({ staff, onEdit, onRemove }) {
  const role = ROLES.find((r) => r.id === staff.role)?.label || staff.role;
  const scoreColor = staff.score >= 95 ? 'var(--cwm-green-text)'
                    : staff.score >= 85 ? 'var(--cwm-amber-text)'
                    : staff.score > 0 ? 'var(--cwm-red-text)'
                    : 'var(--cwm-gray-500)';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.26, ease: EASE }}
      className="cwm-sheet" style={{ padding: 16 }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: 'linear-gradient(160deg, #D9F2D5, #A6E8C2)',
          color: '#047857', display: 'grid', placeItems: 'center',
          fontSize: 14, fontWeight: 900, flexShrink: 0,
        }}>
          {staff.initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>{staff.name}</div>
            {staff.invited && (
              <span style={{ padding: '1px 7px', borderRadius: 999, background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)', fontSize: 9.5, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Invited
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>{role}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: scoreColor }}>{staff.score || 0}%</div>
          <div style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--cwm-gray-500)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Quiz</div>
        </div>
      </div>

      {(staff.email || staff.phone) && (
        <div style={{ display: 'grid', gap: 3, marginBottom: 10, fontSize: 11.5, color: 'var(--cwm-gray-700)', fontWeight: 600 }}>
          {staff.email && <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><MailIcon size={11} /> {staff.email}</div>}
          {staff.phone && <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><PhoneIcon size={11} /> {staff.phone}</div>}
        </div>
      )}

      {staff.certifications?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {staff.certifications.map((c) => (
            <span key={c} style={{
              padding: '3px 9px', borderRadius: 999,
              background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
              fontSize: 10, fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase',
            }}>{c}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid var(--cwm-gray-100)' }}>
        <button type="button" onClick={onEdit}
                style={{
                  flex: 1, padding: '7px 10px', borderRadius: 10,
                  background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                  border: '1.5px solid var(--cwm-blue-light)',
                  fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}>
          <PencilIcon size={11} /> Edit
        </button>
        <button type="button" onClick={onRemove}
                style={{
                  padding: '7px 10px', borderRadius: 10,
                  background: '#fff', color: 'var(--cwm-red-text)',
                  border: '1.5px solid #F4B6B6',
                  fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                  cursor: 'pointer',
                }}>
          <TrashIcon size={11} />
        </button>
      </div>
    </motion.div>
  );
}

function ShiftSchedule({ staff, onAssign }) {
  return (
    <div className="cwm-sheet" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(180deg, var(--cwm-blue-pale), #fff)' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 900, color: 'var(--cwm-gray-700)', letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: '1px solid var(--cwm-gray-100)' }}>Team</th>
              {SHIFT_DAYS.map((d) => (
                <th key={d} style={{ padding: '12px 10px', textAlign: 'center', fontSize: 11, fontWeight: 900, color: 'var(--cwm-gray-700)', letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: '1px solid var(--cwm-gray-100)' }}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff.map((s, i) => (
              <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFCFF' }}>
                <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 900, color: 'var(--cwm-ink)', borderRight: '1px solid var(--cwm-gray-100)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 10,
                      background: 'linear-gradient(160deg, #D9F2D5, #A6E8C2)',
                      color: '#047857', display: 'grid', placeItems: 'center',
                      fontSize: 10.5, fontWeight: 900, flexShrink: 0,
                    }}>
                      {s.initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 900 }}>{s.name}</div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--cwm-gray-500)', textTransform: 'capitalize' }}>{s.role}</div>
                    </div>
                  </div>
                </td>
                {SHIFT_DAYS.map((d) => (
                  <td key={d} style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid var(--cwm-gray-100)' }}>
                    <ShiftCell
                      blockId={s.shifts?.[d] || null}
                      onChange={(blockId) => onAssign(s.id, d, blockId)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--cwm-gray-100)', display: 'flex', gap: 10, flexWrap: 'wrap', background: 'var(--cwm-gray-50)' }}>
        <div style={{ fontSize: 10.5, fontWeight: 900, color: 'var(--cwm-gray-500)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Shift key</div>
        {SHIFT_BLOCKS.map((b) => {
          const tone = TONE_COLORS[b.tone] || TONE_COLORS.blue;
          return (
            <span key={b.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 999,
              background: tone.bg, color: tone.fg,
              fontSize: 11, fontWeight: 800,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: tone.solid }}/>
              {b.label} <span style={{ color: 'var(--cwm-gray-500)', fontWeight: 700, marginLeft: 2 }}>{b.time}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ShiftCell({ blockId, onChange }) {
  const block = SHIFT_BLOCKS.find((b) => b.id === blockId);
  const tone = block ? (TONE_COLORS[block.tone] || TONE_COLORS.blue) : null;
  return (
    <select
      value={blockId || ''}
      onChange={(e) => onChange(e.target.value || null)}
      style={{
        appearance: 'none',
        padding: '6px 10px', borderRadius: 999,
        background: block ? tone.bg : '#fff',
        color: block ? tone.fg : 'var(--cwm-gray-500)',
        border: `1.5px solid ${block ? tone.solid : 'var(--cwm-gray-200)'}`,
        fontFamily: 'var(--cwm-font)', fontSize: 10.5, fontWeight: 800,
        cursor: 'pointer', textAlign: 'center',
        minWidth: 68,
      }}>
      <option value="">—</option>
      {SHIFT_BLOCKS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
    </select>
  );
}

function InviteStaffDialog({ restaurant, onClose, onInvite }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('server');
  const canInvite = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <StaffModal onClose={onClose} title="Invite a team member">
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 500, lineHeight: 1.5 }}>
        They'll get an email to join {restaurant?.name || 'your restaurant'}'s staff dashboard and take the allergy-safety quiz.
      </p>
      <StaffField label="Full name">
        <StaffInput value={name} onChange={setName} placeholder="e.g. Sofia Martinez" />
      </StaffField>
      <StaffField label="Email">
        <StaffInput value={email} onChange={setEmail} placeholder="sofia@example.com" type="email" />
      </StaffField>
      <StaffField label="Role">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {ROLES.map((r) => {
            const on = role === r.id;
            return (
              <button key={r.id} type="button" onClick={() => setRole(r.id)}
                      style={{
                        padding: '8px 14px', borderRadius: 999,
                        background: on ? 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))' : '#fff',
                        color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-200)'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 12, fontWeight: 800,
                        cursor: 'pointer',
                      }}>
                {r.label}
              </button>
            );
          })}
        </div>
      </StaffField>
      <StaffFooter
        onClose={onClose}
        onSave={() => onInvite({ name: name.trim(), email: email.trim(), role })}
        saveLabel="Send invite"
        disableSave={!canInvite}
      />
    </StaffModal>
  );
}

function EditStaffDialog({ staff, onClose, onSave }) {
  const [draft, setDraft] = useState({ ...staff });
  const toggleCert = (c) => setDraft((d) => ({
    ...d,
    certifications: (d.certifications || []).includes(c)
      ? d.certifications.filter((x) => x !== c)
      : [...(d.certifications || []), c],
  }));
  const CERTS = ['allergy', 'wine', 'pos', 'manager', 'foh'];

  return (
    <StaffModal onClose={onClose} title={`Edit ${staff.name}`}>
      <StaffField label="Full name"><StaffInput value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} /></StaffField>
      <StaffField label="Email"><StaffInput value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} type="email" /></StaffField>
      <StaffField label="Phone"><StaffInput value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} type="tel" /></StaffField>
      <StaffField label="Role">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {ROLES.map((r) => {
            const on = draft.role === r.id;
            return (
              <button key={r.id} type="button" onClick={() => setDraft({ ...draft, role: r.id })}
                      style={{
                        padding: '7px 13px', borderRadius: 999,
                        background: on ? 'linear-gradient(160deg, #fff, var(--cwm-blue-pale))' : '#fff',
                        color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-200)'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                        cursor: 'pointer',
                      }}>
                {r.label}
              </button>
            );
          })}
        </div>
      </StaffField>
      <StaffField label="Certifications">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {CERTS.map((c) => {
            const on = (draft.certifications || []).includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCert(c)}
                      style={{
                        padding: '5px 12px', borderRadius: 999,
                        background: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-50)',
                        color: on ? '#fff' : 'var(--cwm-gray-700)',
                        border: `1.5px solid ${on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-200)'}`,
                        fontFamily: 'var(--cwm-font)', fontSize: 11, fontWeight: 900,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}>
                {on && '✓ '}{c}
              </button>
            );
          })}
        </div>
      </StaffField>
      <StaffFooter onClose={onClose} onSave={() => onSave(draft)} />
    </StaffModal>
  );
}

function StaffModal({ children, onClose, title }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20 }}>
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 24, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(10,37,64,0.3)' }}>
        <div style={{ marginBottom: 16 }}>
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
            <TabIcon name="badge" size={12} /> STAFF
          </div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>{title}</h2>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
function StaffField({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>{label}</div>
      {children}
    </div>
  );
}
function StaffInput({ value, onChange, placeholder, type = 'text' }) {
  return <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type}
    style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: 14, border: '1.5px solid var(--cwm-blue-light)', background: '#F7FBFF', color: 'var(--cwm-ink)', fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600, outline: 'none' }} />;
}
function StaffFooter({ onClose, onSave, saveLabel = 'Save', disableSave = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
      <button className="cwm-btn-secondary" onClick={onClose}>Cancel</button>
      <button
        className="cwm-btn"
        onClick={onSave}
        disabled={disableSave}
        style={{ opacity: disableSave ? 0.6 : 1, cursor: disableSave ? 'not-allowed' : 'pointer' }}>
        {saveLabel}
      </button>
    </div>
  );
}

function MailIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16v12H4zM4 6l8 7 8-7"/></svg>; }
function PhoneIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9V19a2 2 0 01-2.2 2A19 19 0 013 5.2 2 2 0 015 3h2.1a2 2 0 012 1.7c.1.9.3 1.8.6 2.6a2 2 0 01-.4 2.1L8 10.6a16 16 0 006 6l1.2-1.3a2 2 0 012.1-.4c.8.3 1.7.5 2.6.6a2 2 0 011.7 2z"/></svg>; }
function QuizIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 9a3 3 0 016 0c0 1.5-1 2-2 2.5S12 13 12 14M12 17h.01"/></svg>; }
function TrashIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }

// ============================================================
// ICONS
// ============================================================

function TabIcon({ name, size = 14 }) {
  const paths = {
    chart:     'M3 3v18h18M7 15l4-4 4 4 5-7',
    menu:      'M4 4h10v6H4zM4 13h10v6H4zM17 4h3v16h-3z',
    flame:     'M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z',
    shield:    'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    people:    'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    store:     'M3 10l1-6h16l1 6M3 10v10h18V10M3 10h18',
    lightbulb: 'M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z',
    badge:     'M12 2l3 6 6 1-5 4 1 7-5-3-5 3 1-7-5-4 6-1z',
    star:      'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 18l-6.2 3 1.2-6.8-5-4.9 6.9-1z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={name === 'flame' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.chart}/></svg>;
}

function FlameIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 3s4 4 4 8a4 4 0 01-8 0c0-2 1-3 2-4 0 1 0 2 1 2 0-2 1-4 1-6z"/></svg>; }
function PencilIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function ArrowIcon({ size = 14, color = 'currentColor' }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }
